/**
 * Display formatting shared by both React apps and the PDF renderer, so a bill
 * rendered on screen and the same bill rendered to PDF agree exactly.
 */

/** The PHP app printed an em dash for missing/zero dates. */
export const EMPTY_PLACEHOLDER = '—';

function parseDate(value: string | null | undefined): Date | null {
  if (!value || value.startsWith('0000-00-00')) return null;
  // Accept both `YYYY-MM-DD` and full ISO timestamps.
  const iso = value.length === 10 ? `${value}T00:00:00` : value;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `DD-MM-YYYY` — e.g. `05-04-2026`. The single date format used across both apps. */
export function formatDate(value: string | null | undefined): string {
  const d = parseDate(value);
  if (!d) return EMPTY_PLACEHOLDER;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

/** `DD-MM-YYYY h:i a` — e.g. `05-04-2026 03:42 pm`. Used by the activity feed. */
export function formatDateTime(value: string | null | undefined): string {
  const d = parseDate(value);
  if (!d) return EMPTY_PLACEHOLDER;
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const meridiem = hours24 < 12 ? 'am' : 'pm';
  return `${formatDate(value)} ${String(hours12).padStart(2, '0')}:${minutes} ${meridiem}`;
}

/** Human-readable file size for the document upload screens. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Mask a mobile number for display in lists — `98******10`.
 * Detail screens show it in full, as the legacy portal did.
 */
export function maskMobile(mobile: string | null | undefined): string {
  const digits = (mobile ?? '').replace(/\D/g, '');
  if (digits.length < 6) return mobile ?? EMPTY_PLACEHOLDER;
  return `${digits.slice(0, 2)}${'*'.repeat(digits.length - 4)}${digits.slice(-2)}`;
}

/** Join the address parts a property row carries, skipping blanks. */
export function joinAddress(
  ...parts: Array<string | null | undefined>
): string {
  const joined = parts.map((p) => (p ?? '').trim()).filter(Boolean).join(', ');
  return joined || EMPTY_PLACEHOLDER;
}

/** First name for the owner chip in the app shell. */
export function firstName(fullName: string | null | undefined): string {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

/** Single-letter avatar initial. */
export function initial(name: string | null | undefined): string {
  const f = firstName(name);
  return f ? f.charAt(0).toUpperCase() : '?';
}
